import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReadAloudStatus, SpeechSegment, TtsVoice } from '@/domains/types'
import type { SupportedLangCode } from '@/application/i18n/language'
import {
  useReadAloudStore,
  type ReadAloudEngineChoice,
} from '@/application/stores/read-aloud.store'
import { getWebSpeechEngine } from '@/application/tts/web-speech-engine'
import { getPiperEngine } from '@/application/tts/piper-engine'
import type { TtsEngine } from '@/application/tts/tts-engine'
import { selectVoice } from '@/application/tts/voice-selection'
import { getVoiceEntry } from '@/application/tts/voice-catalogue'
import { bindMediaSession, setMediaSessionPlaybackState } from '@/application/tts/media-session'
import { elapsedSeconds, segmentSeconds, totalSeconds } from '@/application/tts/listening-time'

/** Speed cycle the "1×" button steps through, in order, wrapping back to 1
 * after 0.75 (read-mode.listen spec §3). */
const RATE_CYCLE: readonly number[] = [1, 1.25, 1.5, 1.75, 2, 0.75]

const TICK_INTERVAL_MS = 250

/** A segment's watchdog fires if `onend` hasn't arrived after twice its
 * estimated spoken duration plus a fixed grace period — generous enough to
 * absorb normal synthesis jitter while still catching an engine that's
 * silently stuck. */
const WATCHDOG_GRACE_MS = 3000
const WATCHDOG_MULTIPLIER = 2

/** Two consecutive watchdog timeouts / hard errors move the player to
 * `'error'` rather than silently skipping through the rest of the article. */
const CONSECUTIVE_FAILURE_LIMIT = 2

/** A segment in a language whose site voice is not loaded yet only starts
 * loading that voice this many segments before it is needed. */
const LANG_LOOKAHEAD_SEGMENTS = 3

const MEDIA_SESSION_ARTIST = 'Balivernologie'

function normalizeLangPrefix(lang: string): string {
  return lang.replace(/_/g, '-').toLowerCase().split('-')[0] ?? ''
}

export interface UseReadAloudOptions {
  /** Ordered, already-language-tagged utterances to speak — from
   * `buildSpeechSegments`. */
  segments: SpeechSegment[]
  /** The article's own (title's) language — decides `unsupported` (no voice
   * for it at all) and which language the voice menu offers voices for. */
  primaryLang: SupportedLangCode
  /** Changing this value resets playback to the very start (position 0,
   * idle) and cancels whatever is speaking — used when the article or the
   * UI locale changes. */
  resetKey: string
  /** The "device voices" engine. Defaults to the shared `WebSpeechEngine`
   * singleton; overridable so tests can inject a fully controllable fake. */
  engine?: TtsEngine
  /** The "site voices" engine. Defaults to the shared `PiperEngine`
   * singleton; overridable for tests. */
  siteEngine?: TtsEngine
  /** Article title, shown on the OS media controls while site voices play. */
  title?: string
}

export interface UseReadAloudResult {
  status: ReadAloudStatus
  segmentIndex: number
  segmentCount: number
  elapsedSeconds: number
  totalSeconds: number
  rate: number
  voices: TtsVoice[]
  selectedVoiceId: string | null
  /** True once voices are loaded and every voice available for `primaryLang`
   * is low-quality — drives the voice menu's quality hint. */
  lowQualityOnly: boolean
  /** Play/pause/resume/replay, whichever the current `status` calls for. */
  toggle: () => void
  stop: () => void
  seekToSegment: (index: number) => void
  cycleRate: () => void
  /** Always sets the voice preference for `primaryLang` — the only language
   * the voice menu lets the visitor choose for (read-mode.listen spec §3). */
  setVoice: (voiceId: string | null) => void
  /** Download progress (`0..1`) of the site voice while `preparing`; `null`
   * once downloaded (or unknown), while the first clip is synthesized. */
  preparingProgress: number | null
  /** The visitor's persisted engine choice. */
  enginePreference: ReadAloudEngineChoice
  /** The browser can run the site (Piper) voices. */
  siteAvailable: boolean
  /** The browser has a device voice for the article's language. */
  deviceAvailable: boolean
  /** The first click is waiting for the visitor to accept or refuse the
   * site voice download. */
  consentPending: boolean
  /** Site voices failed and playback moved to the device voices. */
  usingFallback: boolean
  acceptModelDownload: () => void
  declineModelDownload: () => void
  setEnginePreference: (choice: ReadAloudEngineChoice) => void
}

export function useReadAloud({
  segments,
  primaryLang,
  resetKey,
  engine,
  siteEngine,
  title = '',
}: UseReadAloudOptions): UseReadAloudResult {
  const engineRef = useRef<TtsEngine>(engine ?? getWebSpeechEngine())
  engineRef.current = engine ?? engineRef.current
  const siteEngineRef = useRef<TtsEngine>(siteEngine ?? getPiperEngine())
  siteEngineRef.current = siteEngine ?? siteEngineRef.current
  // The engine the current playback runs on — always the device engine until
  // a start decides the site voices are usable.
  const activeEngineRef = useRef<TtsEngine>(engineRef.current)
  const siteSupported = siteEngineRef.current.isSupported()

  const rate = useReadAloudStore((state) => state.rate)
  const setRateStore = useReadAloudStore((state) => state.setRate)
  const voiceIdByLang = useReadAloudStore((state) => state.voiceIdByLang)
  const setVoiceStore = useReadAloudStore((state) => state.setVoice)
  const enginePreference = useReadAloudStore((state) => state.engine)

  const [status, setStatus] = useState<ReadAloudStatus>('loading')
  const [segmentIndex, setSegmentIndex] = useState(0)
  const [voices, setVoices] = useState<TtsVoice[]>([])
  const [tickCount, setTickCount] = useState(0)
  const [preparingProgress, setPreparingProgress] = useState<number | null>(null)
  const [consentPending, setConsentPending] = useState(false)
  const [usingFallback, setUsingFallback] = useState(false)
  const [siteActive, setSiteActive] = useState(false)
  const [durationsVersion, setDurationsVersion] = useState(0)

  const statusRef = useRef(status)
  statusRef.current = status
  const segmentIndexRef = useRef(segmentIndex)
  segmentIndexRef.current = segmentIndex
  const segmentsRef = useRef(segments)
  segmentsRef.current = segments
  // `speakSegmentAt` reads the rate from this ref (rather than closing over
  // the `rate` render value) so `cycleRate` can bump it and immediately
  // restart the current segment in the very same synchronous call — a
  // freshly-created closure wouldn't exist yet, since the store's own
  // re-render hasn't happened.
  const primaryLangRef = useRef(primaryLang)
  primaryLangRef.current = primaryLang
  const rateRef = useRef(rate)
  rateRef.current = rate
  const voicesRef = useRef(voices)
  voicesRef.current = voices
  // Real durations (seconds at 1x) reported by the site engine, per segment id.
  const knownDurationsRef = useRef<Map<string, number>>(new Map())
  const siteFailedRef = useRef(false)
  const prepareAbortRef = useRef<AbortController | null>(null)
  const toggleRef = useRef<() => void>(() => {})
  const seekRef = useRef<(index: number) => void>(() => {})

  const startedAtRef = useRef(0)
  const failureCountRef = useRef(0)
  const watchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const tickIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const pendingPlayRef = useRef(false)
  const voiceCacheRef = useRef<Map<SupportedLangCode, TtsVoice | null>>(new Map())
  const speakSegmentAtRef = useRef<(index: number) => void>(() => {})

  const clearWatchdog = useCallback(() => {
    if (watchdogRef.current !== null) {
      clearTimeout(watchdogRef.current)
      watchdogRef.current = null
    }
  }, [])

  const clearTick = useCallback(() => {
    if (tickIntervalRef.current !== null) {
      clearInterval(tickIntervalRef.current)
      tickIntervalRef.current = null
    }
  }, [])

  const startTick = useCallback(() => {
    clearTick()
    tickIntervalRef.current = setInterval(
      () => setTickCount((count) => count + 1),
      TICK_INTERVAL_MS,
    )
  }, [clearTick])

  const getOrSelectVoice = useCallback(
    (lang: SupportedLangCode): TtsVoice | null => {
      const cache = voiceCacheRef.current
      if (cache.has(lang)) return cache.get(lang) ?? null

      const navigatorLanguages =
        typeof navigator !== 'undefined' && navigator.languages ? navigator.languages : []
      const picked = selectVoice({
        voices,
        lang,
        preferredVoiceId: voiceIdByLang[lang] ?? null,
        navigatorLanguages,
      })
      cache.set(lang, picked)
      return picked
    },
    [voices, voiceIdByLang],
  )

  // The voice cache is keyed by language and only ever invalidated when the
  // voice list itself changes or a preference is explicitly set (see
  // `setVoice` below) — never on every render.
  useEffect(() => {
    voiceCacheRef.current.clear()
  }, [voices])

  const finishSegment = useCallback(
    (index: number) => {
      const next = index + 1
      if (next < segmentsRef.current.length) {
        setSegmentIndex(next)
        speakSegmentAtRef.current(next)
      } else {
        clearTick()
        setSegmentIndex(segmentsRef.current.length)
        setStatus('ended')
      }
    },
    [clearTick],
  )

  const handleFailure = useCallback(
    (index: number) => {
      failureCountRef.current += 1
      if (failureCountRef.current >= CONSECUTIVE_FAILURE_LIMIT) {
        clearWatchdog()
        clearTick()
        setStatus('error')
        return
      }
      finishSegment(index)
    },
    [clearWatchdog, clearTick, finishSegment],
  )

  const abortPrepare = useCallback(() => {
    prepareAbortRef.current?.abort()
    prepareAbortRef.current = null
  }, [])

  /** Speaks `index` with the device (Web Speech) engine — the v1 behavior,
   * watchdog included. */
  const speakWithDevice = useCallback(
    (index: number) => {
      const segment = segmentsRef.current[index]
      const device = engineRef.current
      const voice = getOrSelectVoice(segment.lang)
      const currentRate = rateRef.current
      const estimatedMs = segmentSeconds(segment.words, currentRate) * 1000

      watchdogRef.current = setTimeout(
        () => handleFailure(index),
        estimatedMs * WATCHDOG_MULTIPLIER + WATCHDOG_GRACE_MS,
      )

      // Always cancels any speech already in flight — `WebSpeechEngine.speak`
      // does this itself, but calling it explicitly first keeps this call
      // synchronous end-to-end for iOS's "first speak() in the click
      // handler" requirement even when swapping engines in tests.
      device.speak(
        { text: segment.text, lang: segment.lang, voiceId: voice?.id ?? null, rate: currentRate },
        {
          onStart: () => {
            startedAtRef.current = Date.now()
            setStatus('playing')
            startTick()
          },
          onEnd: () => {
            clearWatchdog()
            failureCountRef.current = 0
            finishSegment(index)
          },
          onError: (kind) => {
            if (kind === 'interrupted') return
            clearWatchdog()
            handleFailure(index)
          },
        },
      )
    },
    [clearWatchdog, finishSegment, getOrSelectVoice, handleFailure, startTick],
  )

  /** Queues the segments after `index` on the site engine so they are
   * already synthesized when reached (and, on iOS, the whole article is
   * ready before the screen locks). */
  const prefetchAhead = useCallback((index: number) => {
    const site = siteEngineRef.current
    if (!site.prefetch) return
    const allSegments = segmentsRef.current
    for (let position = index + 1; position < allSegments.length; position++) {
      const upcoming = allSegments[position]
      const langReady = site.isPrepared?.(upcoming.lang) ?? true
      if (!langReady && position - index > LANG_LOOKAHEAD_SEGMENTS) continue
      site.prefetch({
        text: upcoming.text,
        lang: upcoming.lang,
        voiceId: getVoiceEntry(upcoming.lang).id,
        rate: rateRef.current,
      })
    }
  }, [])

  const fallbackToDevice = useCallback(
    (index: number) => {
      siteFailedRef.current = true
      setUsingFallback(true)
      setSiteActive(false)
      abortPrepare()
      siteEngineRef.current.cancel()
      clearWatchdog()
      setPreparingProgress(null)

      const device = engineRef.current
      const hasDeviceVoice = voicesRef.current.some(
        (voice) => normalizeLangPrefix(voice.lang) === primaryLangRef.current,
      )
      activeEngineRef.current = device
      if (!device.isSupported() || !hasDeviceVoice) {
        clearTick()
        setStatus('error')
        return
      }
      speakSegmentAtRef.current(index)
    },
    [abortPrepare, clearTick, clearWatchdog],
  )

  /** Speaks `index` with the site (Piper) engine, downloading the segment
   * language's voice first when needed. */
  const speakWithSite = useCallback(
    (index: number) => {
      const segment = segmentsRef.current[index]
      const site = siteEngineRef.current

      const play = () => {
        setPreparingProgress(null)
        if (statusRef.current !== 'playing') setStatus('preparing')
        site.speak(
          {
            text: segment.text,
            lang: segment.lang,
            voiceId: getVoiceEntry(segment.lang).id,
            rate: rateRef.current,
          },
          {
            onStart: () => {
              startedAtRef.current = Date.now()
              failureCountRef.current = 0
              setStatus('playing')
              startTick()
              prefetchAhead(index)
            },
            onDuration: (seconds) => {
              knownDurationsRef.current.set(segment.id, seconds)
              setDurationsVersion((version) => version + 1)
            },
            onEnd: () => finishSegment(index),
            onError: (kind) => {
              if (kind === 'interrupted') return
              fallbackToDevice(index)
            },
          },
        )
      }

      if (site.isPrepared?.(segment.lang) ?? true) {
        play()
        return
      }

      abortPrepare()
      const controller = new AbortController()
      prepareAbortRef.current = controller
      if (statusRef.current !== 'playing') clearTick()
      setStatus('preparing')
      setPreparingProgress(0)
      site
        .prepare?.([segment.lang], {
          signal: controller.signal,
          onProgress: (fraction) => {
            if (!controller.signal.aborted) setPreparingProgress(fraction)
          },
        })
        .then(() => {
          if (!controller.signal.aborted) play()
        })
        .catch(() => {
          if (!controller.signal.aborted) fallbackToDevice(index)
        })
    },
    [abortPrepare, clearTick, fallbackToDevice, finishSegment, prefetchAhead, startTick],
  )

  const speakSegmentAt = useCallback(
    (index: number) => {
      if (!segmentsRef.current[index]) {
        clearWatchdog()
        clearTick()
        setStatus('ended')
        return
      }

      clearWatchdog()
      if (activeEngineRef.current.id === 'piper') {
        speakWithSite(index)
      } else {
        speakWithDevice(index)
      }
    },
    [clearTick, clearWatchdog, speakWithDevice, speakWithSite],
  )

  useEffect(() => {
    speakSegmentAtRef.current = speakSegmentAt
  }, [speakSegmentAt])

  /** Picks the engine for a start and unlocks audio if it is the site one.
   * Must run synchronously inside the user's gesture. Returns `false` when a
   * consent prompt was raised instead of starting. */
  const beginPlayback = useCallback((index: number): boolean => {
    const site = siteEngineRef.current
    const { engine: preference, modelConsent } = useReadAloudStore.getState()
    const siteWanted = site.isSupported() && preference === 'site' && !siteFailedRef.current

    if (siteWanted && !modelConsent) {
      setConsentPending(true)
      return false
    }

    setConsentPending(false)
    if (siteWanted) {
      site.unlock?.()
      activeEngineRef.current = site
      setSiteActive(true)
    } else {
      activeEngineRef.current = engineRef.current
      setSiteActive(false)
    }
    speakSegmentAtRef.current(index)
    return true
  }, [])

  // ─── Voice loading ────────────────────────────────────────────────────────
  useEffect(() => {
    const controller = new AbortController()
    const engineInstance = engineRef.current
    let cancelled = false

    if (!engineInstance.isSupported()) {
      // Site voices can still speak without any device voice.
      setStatus(siteSupported ? 'idle' : 'unsupported')
      return () => controller.abort()
    }

    // A missing primary-language voice is only provisional: browsers can
    // publish their voice list in several batches (first `voiceschanged`
    // with local voices only, network voices later), so every later update
    // re-evaluates support instead of hiding the widget for good.
    const applyVoices = (loadedVoices: TtsVoice[]) => {
      if (cancelled) return
      setVoices(loadedVoices)

      const hasPrimaryVoice = loadedVoices.some(
        (voice) => normalizeLangPrefix(voice.lang) === primaryLang,
      )
      if (!hasPrimaryVoice && !siteSupported) {
        setStatus((current) => (current === 'loading' ? 'unsupported' : current))
        return
      }

      setStatus((current) =>
        current === 'loading' || current === 'unsupported' ? 'idle' : current,
      )
      if (pendingPlayRef.current) {
        pendingPlayRef.current = false
        beginPlayback(segmentIndexRef.current)
      }
    }

    const unsubscribe = engineInstance.onVoicesChanged(applyVoices)
    engineInstance.loadVoices(controller.signal).then(applyVoices)

    return () => {
      cancelled = true
      unsubscribe()
      controller.abort()
    }
    // primaryLang/resetKey both legitimately re-run this: a new article (or
    // a UI-locale switch that changes the spoken language) may support a
    // different set of voices.
  }, [primaryLang, resetKey, siteSupported, beginPlayback])

  // ─── Reset on article/locale change ───────────────────────────────────────
  useEffect(() => {
    activeEngineRef.current.cancel()
    siteEngineRef.current.releaseCache?.()
    abortPrepare()
    clearWatchdog()
    clearTick()
    failureCountRef.current = 0
    pendingPlayRef.current = false
    voiceCacheRef.current.clear()
    knownDurationsRef.current.clear()
    setDurationsVersion((version) => version + 1)
    setPreparingProgress(null)
    setConsentPending(false)
    setSegmentIndex(0)
    // With no device voices at all but site voices available, there is
    // nothing to wait for.
    const siteOnly = !engineRef.current.isSupported() && siteSupported
    setStatus((current) => (current === 'unsupported' ? current : siteOnly ? 'idle' : 'loading'))

    return () => {
      activeEngineRef.current.cancel()
      abortPrepare()
      clearWatchdog()
      clearTick()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey])

  // ─── Backgrounding / navigation-away ──────────────────────────────────────
  useEffect(() => {
    function handlePageHide() {
      activeEngineRef.current.cancel()
    }
    function handleVisibilityChange() {
      if (
        document.visibilityState === 'visible' &&
        statusRef.current === 'playing' &&
        !activeEngineRef.current.isSpeaking()
      ) {
        clearWatchdog()
        clearTick()
        setStatus('paused')
      }
    }

    window.addEventListener('pagehide', handlePageHide)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      window.removeEventListener('pagehide', handlePageHide)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [clearTick, clearWatchdog])

  const toggle = useCallback(() => {
    const current = statusRef.current
    const active = activeEngineRef.current
    if (current === 'idle') {
      beginPlayback(segmentIndexRef.current)
      return
    }
    if (current === 'loading') {
      pendingPlayRef.current = true
      return
    }
    if (current === 'preparing') {
      abortPrepare()
      active.cancel()
      clearTick()
      setPreparingProgress(null)
      setStatus('idle')
      return
    }
    if (current === 'playing') {
      if (active.pause) {
        active.pause()
      } else {
        active.cancel()
      }
      clearWatchdog()
      clearTick()
      setStatus('paused')
      return
    }
    if (current === 'paused') {
      if (active.resume?.()) {
        startTick()
        setStatus('playing')
        return
      }
      beginPlayback(segmentIndexRef.current)
      return
    }
    if (current === 'ended' || current === 'error') {
      failureCountRef.current = 0
      setSegmentIndex(0)
      beginPlayback(0)
    }
  }, [abortPrepare, beginPlayback, clearTick, clearWatchdog, startTick])

  useEffect(() => {
    toggleRef.current = toggle
  }, [toggle])

  const stop = useCallback(() => {
    activeEngineRef.current.cancel()
    abortPrepare()
    clearWatchdog()
    clearTick()
    failureCountRef.current = 0
    setPreparingProgress(null)
    setConsentPending(false)
    setSegmentIndex(0)
    setStatus((current) => (current === 'unsupported' || current === 'loading' ? current : 'idle'))
  }, [abortPrepare, clearTick, clearWatchdog])

  const seekToSegment = useCallback((index: number) => {
    const maxIndex = Math.max(segmentsRef.current.length - 1, 0)
    const clamped = Math.min(Math.max(index, 0), maxIndex)

    if (statusRef.current === 'playing') {
      setSegmentIndex(clamped)
      speakSegmentAtRef.current(clamped)
      return
    }

    // Paused audio must not be resumed at the old position.
    activeEngineRef.current.cancel()
    setSegmentIndex(clamped)
    if (statusRef.current === 'ended') setStatus('paused')
  }, [])

  useEffect(() => {
    seekRef.current = seekToSegment
  }, [seekToSegment])

  const cycleRate = useCallback(() => {
    const currentIndex = RATE_CYCLE.indexOf(rateRef.current)
    const next = RATE_CYCLE[(currentIndex + 1) % RATE_CYCLE.length] ?? RATE_CYCLE[0]
    rateRef.current = next
    setRateStore(next)
    const active = activeEngineRef.current
    if (active.setLiveRate) {
      active.setLiveRate(next)
    } else if (statusRef.current === 'playing') {
      speakSegmentAtRef.current(segmentIndexRef.current)
    }
  }, [setRateStore])

  const setVoice = useCallback(
    (voiceId: string | null) => {
      setVoiceStore(primaryLang, voiceId)
      voiceCacheRef.current.delete(primaryLang)
      if (
        statusRef.current === 'playing' &&
        activeEngineRef.current.id === 'web-speech' &&
        segmentsRef.current[segmentIndexRef.current]?.lang === primaryLang
      ) {
        speakSegmentAtRef.current(segmentIndexRef.current)
      }
    },
    [primaryLang, setVoiceStore],
  )

  const acceptModelDownload = useCallback(() => {
    useReadAloudStore.getState().setEngine('site')
    useReadAloudStore.getState().setModelConsent(true)
    beginPlayback(segmentIndexRef.current)
  }, [beginPlayback])

  const declineModelDownload = useCallback(() => {
    useReadAloudStore.getState().setEngine('device')
    beginPlayback(segmentIndexRef.current)
  }, [beginPlayback])

  const setEnginePreference = useCallback(
    (choice: ReadAloudEngineChoice) => {
      const store = useReadAloudStore.getState()
      if (store.engine === choice) return
      store.setEngine(choice)
      siteFailedRef.current = false
      setUsingFallback(false)
      setConsentPending(false)
      if (statusRef.current === 'playing' || statusRef.current === 'preparing') {
        activeEngineRef.current.cancel()
        abortPrepare()
        clearWatchdog()
        // Switching engines mid-playback continues from the same segment.
        if (!beginPlayback(segmentIndexRef.current)) {
          clearTick()
          setStatus('idle')
        }
      }
    },
    [abortPrepare, beginPlayback, clearTick, clearWatchdog],
  )

  // ─── OS media controls (lock screen, headset buttons) ────────────────────
  const mediaSessionActive = siteActive && (status === 'playing' || status === 'paused')
  useEffect(() => {
    if (!mediaSessionActive) return undefined
    return bindMediaSession(
      { title, artist: MEDIA_SESSION_ARTIST },
      {
        onPlay: () => {
          if (statusRef.current === 'paused') toggleRef.current()
        },
        onPause: () => {
          if (statusRef.current === 'playing') toggleRef.current()
        },
        onPrevious: () => seekRef.current(segmentIndexRef.current - 1),
        onNext: () => seekRef.current(segmentIndexRef.current + 1),
      },
    )
  }, [mediaSessionActive, title])

  useEffect(() => {
    if (mediaSessionActive)
      setMediaSessionPlaybackState(status === 'playing' ? 'playing' : 'paused')
  }, [mediaSessionActive, status])

  // ─── Unmount ───────────────────────────────────────────────────────────────
  useEffect(
    () => () => {
      activeEngineRef.current.cancel()
      clearWatchdog()
      clearTick()
    },
    [clearTick, clearWatchdog],
  )

  const totalSecondsValue = useMemo(
    () => totalSeconds(segments, rate, knownDurationsRef.current),
    // `durationsVersion` signals that the (mutable) known-durations map changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [segments, rate, durationsVersion],
  )

  const elapsedSecondsValue = useMemo(
    () =>
      elapsedSeconds({
        segments,
        segmentIndex,
        rate,
        now: Date.now(),
        startedAt: startedAtRef.current,
        knownDurations: knownDurationsRef.current,
        currentProgress: activeEngineRef.current.getProgress?.() ?? null,
      }),
    // `status` and `tickCount` both need to force a recompute even though
    // `status` itself isn't read inside: it freezes the value the instant
    // playback pauses/ends (by no longer changing), and `tickCount` drives
    // the live-playing countdown every 250ms.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [segments, segmentIndex, rate, status, tickCount, durationsVersion],
  )

  const lowQualityOnly = useMemo(() => {
    const primaryVoices = voices.filter((voice) => normalizeLangPrefix(voice.lang) === primaryLang)
    return primaryVoices.length > 0 && primaryVoices.every((voice) => voice.quality === 'low')
  }, [voices, primaryLang])

  const selectedVoiceId = voiceIdByLang[primaryLang] ?? null

  const deviceAvailable =
    engineRef.current.isSupported() &&
    voices.some((voice) => normalizeLangPrefix(voice.lang) === primaryLang)

  return {
    status,
    segmentIndex,
    segmentCount: segments.length,
    elapsedSeconds: elapsedSecondsValue,
    totalSeconds: totalSecondsValue,
    rate,
    voices,
    selectedVoiceId,
    lowQualityOnly,
    toggle,
    stop,
    seekToSegment,
    cycleRate,
    setVoice,
    preparingProgress,
    enginePreference,
    siteAvailable: siteSupported,
    deviceAvailable,
    consentPending,
    usingFallback,
    acceptModelDownload,
    declineModelDownload,
    setEnginePreference,
  }
}
